import { useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import type { WizardData } from "@/pages/CreateCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarIcon, HelpCircle } from "lucide-react";
import { AiWriteButton } from "@/components/ui/ai-write-button";
import { cn } from "@/lib/utils";

interface Props {
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
}

const timeOptions = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  const ampm = h < 12 ? "AM" : "PM";
  const display = `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m} ${ampm}`;
  const value = `${String(h).padStart(2, "0")}:${m}`;
  return { display, value };
});

const timezones = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "Europe/London", "Europe/Paris",
  "Europe/Berlin", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney",
];

const HelpTip = ({ text }: { text: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle size={14} className="text-muted-foreground/50 cursor-help" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const StepDetails = ({ data, updateData }: Props) => {
  const { t } = useTranslation();
  const [editingSlug, setEditingSlug] = useState(false);
  const [customSlug, setCustomSlug] = useState(data.customSlug || "");

  const slugFromTitle = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 40);

  const currentSlug = customSlug || slugFromTitle(data.title);
  const endDate = data.deadline ? new Date(data.deadline) : undefined;

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) { updateData({ deadline: "" }); return; }
    const existing = data.endTime || "17:00";
    const [h, m] = existing.split(":");
    date.setHours(parseInt(h, 10), parseInt(m, 10));
    updateData({ deadline: date.toISOString() });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground font-serif">
        {t("wizard.details.title")}
      </h2>

      {/* 1. Card name — unique title + shareable URL slug */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-sm font-medium text-foreground">{t("wizard.details.cardName")}</Label>
          <HelpTip text={t("wizard.details.cardNameHint")} />
        </div>
        <Input
          value={data.title}
          onChange={(e) => updateData({ title: e.target.value })}
          placeholder={t("wizard.details.cardNamePlaceholder")}
          className="border-border"
        />
        {data.title && (
          <div className="mt-1.5">
            <p className="text-xs text-muted-foreground">
              joyn.com/{editingSlug ? (
                <input
                  value={customSlug || slugFromTitle(data.title)}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40);
                    setCustomSlug(val);
                    updateData({ customSlug: val });
                  }}
                  onBlur={() => setEditingSlug(false)}
                  autoFocus
                  className="inline border-b border-foreground bg-transparent font-medium text-foreground outline-none w-32"
                />
              ) : (
                <span className="font-medium text-foreground">{currentSlug}</span>
              )}
            </p>
            {!editingSlug && (
              <button onClick={() => setEditingSlug(true)} className="text-xs text-primary font-medium mt-0.5">
                ✏️ {t("wizard.details.editUrl")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 2. Message to your group — note shown to signers */}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-foreground">{t("wizard.details.messageToGroup")}</Label>
          <AiWriteButton
            context={{ occasion: data.occasion, recipientName: data.recipientName }}
            fieldPurpose="group_invite"
            onGenerated={(text) => updateData({ messageToGroup: text })}
          />
        </div>
        <Textarea
          value={data.messageToGroup}
          onChange={(e) => updateData({ messageToGroup: e.target.value })}
          placeholder={t("wizard.details.messagePlaceholder")}
          className="mt-1.5 border-border min-h-[120px]"
          rows={5}
        />
      </div>

      {/* 3. Who's the card for? — Recipient name */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-sm font-medium text-foreground">
            {t("wizard.details.cardFor")}
          </Label>
          <HelpTip text={t("wizard.details.recipientTip")} />
        </div>
        <Input
          value={data.recipientName}
          onChange={(e) => updateData({ recipientName: e.target.value })}
          placeholder={t("wizard.details.recipientNamePlaceholder")}
          className="border-border"
        />
      </div>

      {/* 4. Recipient's email — used to auto-populate gift
          preferences from the recipient's profile if they have one,
          and to deliver the finished card. */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-sm font-medium text-foreground">
            {t("wizard.details.recipientEmail")}{" "}
            <span className="text-destructive">*</span>
          </Label>
          <HelpTip text={t("wizard.details.deliverToRecipient")} />
        </div>
        <Input
          value={data.recipientEmail}
          type="email"
          onChange={(e) => updateData({ recipientEmail: e.target.value })}
          placeholder="sarah@example.com"
          className="border-border"
        />
      </div>

      {/* 5. Who's the card from? — Sender / group name */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-sm font-medium text-foreground">
            {t("wizard.details.cardFrom")}
          </Label>
          <HelpTip text={t("wizard.details.cardFromTip")} />
        </div>
        <Input
          value={data.cardFrom}
          onChange={(e) => updateData({ cardFrom: e.target.value })}
          placeholder={t("wizard.details.cardFromPlaceholder")}
          className="border-border"
        />
      </div>

      {/* 6. When should we send it? — Date on its own row, time +
          timezone on the next row so each field is wide enough to
          read its value. */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-sm font-medium text-foreground">{t("wizard.details.sendDate")}</Label>
          <HelpTip text={t("wizard.details.endDateTip")} />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-full h-12 justify-start text-start font-normal border-border px-3", !endDate && "text-muted-foreground")}>
              <CalendarIcon className="me-2 h-4 w-4 shrink-0" />
              <span className="truncate">{endDate ? format(endDate, "PPP") : t("wizard.details.selectDate")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={endDate} onSelect={handleDateSelect} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
          </PopoverContent>
        </Popover>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Select value={data.endTime || "17:00"} onValueChange={(val) => updateData({ endTime: val })}>
            <SelectTrigger className="border-border"><SelectValue /></SelectTrigger>
            <SelectContent>{timeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.display}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={data.timezone} onValueChange={(val) => updateData({ timezone: val })}>
            <SelectTrigger className="border-border"><SelectValue /></SelectTrigger>
            <SelectContent>{timezones.map((tz) => <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default StepDetails;
